export const CHECKOUT_URL = 'https://hvac-diagnostic-ready.impultienda.ar/checkout'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tUxGwvgCYjwcmz8Atu4Kb3/Diseno-sin-titulo-2-3.webp'

export interface Module {
  num: number
  title: string
  body: string[]
  kicker: string
}

export const modules: Module[] = [
  {
    num: 1,
    title: 'COOLING SYSTEM DIAGNOSTICS™',
    body: [
      'Access a complete visual collection of troubleshooting resources for cooling problems including insufficient cooling, warm air, frozen evaporator coils, short cycling, long cycles, temperature differences and intermittent cooling.',
      'Use organized maps to move from the observed symptom toward relevant checks and possible causes.',
    ],
    kicker: 'See the symptom. Follow the checks. Verify.',
  },
  {
    num: 2,
    title: 'AIRFLOW & INDOOR COMFORT DIAGNOSTICS™',
    body: [
      'Work with visual resources for weak airflow, no airflow, uneven temperatures, filter restrictions, blower problems, supply and return issues, duct restrictions and temperature split checks.',
    ],
    kicker: 'Observe the airflow. Follow the path. Find the cause.',
  },
  {
    num: 3,
    title: 'ELECTRICAL & CONTROL DIAGNOSTICS™',
    body: [
      'Use organized visual maps for system power problems, thermostats, contactors, capacitors, relays, transformers, wiring, motors, control boards, voltage and continuity testing.',
      'Resources are structured to support systematic testing while keeping manufacturer specifications and safe procedures central.',
    ],
    kicker: 'Trace it. Test it. Verify it.',
  },
  {
    num: 4,
    title: 'REFRIGERANT & PRESSURE DIAGNOSTICS™',
    body: [
      'Access visual resources for high and low pressure patterns, superheat, subcooling, charge indicators, restrictions, metering devices, evaporator conditions and condenser conditions.',
      'Use measurements together with operating conditions and manufacturer data to support professional troubleshooting.',
    ],
    kicker: 'Measure. Compare. Interpret. Verify.',
  },
  {
    num: 5,
    title: 'COMPRESSOR & OUTDOOR UNIT DIAGNOSTICS™',
    body: [
      'Use visual pathways for compressor starting problems, overheating, abnormal noises, outdoor-unit faults, condenser fans, start components, capacitors and intermittent operation.',
      'Follow organized component-testing maps and troubleshooting sequences.',
    ],
    kicker: 'Identify it. Check it. Verify it.',
  },
  {
    num: 6,
    title: 'HVAC FIELD TROUBLESHOOTING MAPS™',
    body: [
      'Use quick-reference visual maps organized around common field symptoms including no cooling, weak airflow, warm air, frozen coils, no-start conditions, compressor problems, fan problems, short cycling and abnormal operation.',
      'Move directly from symptom to possible causes, checks, tests and next diagnostic steps.',
    ],
    kicker: 'Find the symptom. Follow the map.',
  },
]

export interface Bonus {
  num: number
  title: string
  body: string[]
  kicker: string
}

export const bonuses: Bonus[] = [
  {
    num: 1,
    title: 'HVAC FIELD TESTING HANDBOOK™',
    body: [
      'Organize common HVAC testing procedures for electrical systems, controls, capacitors, motors, temperatures, airflow, superheat and subcooling.',
      'Use practical procedures, testing checklists, documentation tools and verification workflows alongside the main visual diagnostic system.',
    ],
    kicker: 'Test. Record. Verify.',
  },
  {
    num: 2,
    title: 'HVAC PREVENTIVE MAINTENANCE PLAYBOOK™',
    body: [
      'Use structured inspection and maintenance resources for indoor units, outdoor units, filters, blowers, coils, condensate, electrical components, controls and seasonal maintenance.',
      'Keep recurring maintenance work organized with practical workflows, inspection forms and reusable checklists.',
    ],
    kicker: 'Inspect. Maintain. Document.',
  },
  {
    num: 3,
    title: 'HVAC SERVICE CALL SYSTEM™',
    body: [
      'Organize the complete service call from customer complaint and initial inspection through troubleshooting, testing, findings, final verification and documentation.',
      'Use intake forms, testing logs, diagnostic records, service templates and customer handoff resources.',
    ],
    kicker: 'Inspect. Diagnose. Document. Complete.',
  },
  {
    num: 4,
    title: 'HVAC TECHNICIAN QUICK REFERENCE HANDBOOK™',
    body: [
      'Keep essential HVAC formulas, measurements, conversions, terminology, electrical references, superheat, subcooling and component information organized in one place.',
      'Use practical reference tables, calculations, field terminology and measurement-recording resources.',
    ],
    kicker: 'Check it. Calculate it. Reference it.',
  },
]

export const differentiators = [
  {
    emoji: '🗺️',
    title: 'Ready-to-Use Visual Maps',
    body: 'Open the system and access organized troubleshooting resources instead of searching from scratch.',
  },
  {
    emoji: '🔧',
    title: 'Practical Diagnostic Pathways',
    body: 'Follow symptoms, possible causes, checks and testing sequences in a structured format.',
  },
  {
    emoji: '⚡',
    title: '6 Essential Areas',
    body: 'Keep cooling, airflow, electrical, refrigerant, compressor and field troubleshooting resources organized.',
  },
  {
    emoji: '🔄',
    title: 'Reusable Field References',
    body: 'Return to the maps and resources whenever you encounter relevant HVAC symptoms.',
  },
  {
    emoji: '📂',
    title: 'Organized Diagnostic Library',
    body: 'Stop searching through scattered charts, screenshots, notes and reference material.',
  },
  {
    emoji: '⏱️',
    title: 'Less Searching',
    body: 'Spend less time finding troubleshooting information and keep more resources ready for field use.',
  },
]

export const previewImages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/eWKwZjMpU4uoy6VYa93tQ7/Captura_de_pantalla_2026-09-04_a_la(s)_9.54.46_p._m..webp',
    alt: 'Visual diagnostic page — Possible causes: long cooling cycles',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/oEHdL1CAdx9q4ChRozU5W4/Captura_de_pantalla_2026-09-04_a_la(s)_9.55.12_p._m..webp',
    alt: 'Visual diagnostic page — Check and test: short cycling',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ftkBe4xJJtcwf9VLThndqb/Captura_de_pantalla_2026-09-04_a_la(s)_9.55.31_p._m..webp',
    alt: 'Visual diagnostic page — Possible causes: short cycling',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ft2dnqnCK5PWqAC7EfmVgs/Captura_de_pantalla_2026-09-04_a_la(s)_9.56.02_p._m..webp',
    alt: 'Visual diagnostic page — Symptom: warm air from vents',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/dYeLoQjoV9JCN7sEtj7Vcs/Captura_de_pantalla_2026-09-04_a_la(s)_9.56.19_p._m..webp',
    alt: 'Visual diagnostic page — Possible causes: warm air from vents',
  },
]

export interface Testimonial {
  name: string
  role: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Mark Delaney, 41',
    role: 'Residential HVAC Technician — Dallas, TX',
    quote:
      'I run 6 to 8 calls a day and I used to lose 15 minutes per call digging through saved PDFs and old photos on my phone. Now I open the field troubleshooting map for the symptom the customer describes and I already know what to check first. The short cycling and frozen coil pages alone paid for it on my first week.',
  },
  {
    name: 'Tony Alvarez, 52',
    role: 'Service Manager — Phoenix, AZ',
    quote:
      'I have three junior techs and this is the first resource I have found that keeps their diagnostics consistent. They follow the pathway, gather the measurements, then compare with manufacturer data instead of guessing at the capacitor. Fewer callbacks, cleaner service reports, better documentation for the customer.',
  },
  {
    name: 'Devin Brooks, 29',
    role: 'Commercial HVAC Tech — Manchester, UK',
    quote:
      'Two years in the trade and the knowledge was there, the confidence was not. Having the electrical and control maps open on my tablet while I trace a contactor or transformer changed how I work. I test in order now, and I stop second-guessing myself in front of the customer.',
  },
  {
    name: 'Ryan Whitfield, 36',
    role: 'Independent HVAC Contractor — Calgary, AB',
    quote:
      'The refrigerant and pressure section is what sold me. Pressure patterns, superheat, subcooling and the conditions to check them against, all in one place. I read the gauges the same way every time now, and my interpretation is far more structured than what I was doing from memory.',
  },
  {
    name: 'Glen Harper, 47',
    role: 'HVAC Service Technician — Brisbane, QLD',
    quote:
      'The bonuses ended up being half the value for me. The service call system organizes the whole visit from complaint to final verification, and the quick reference handbook lives on my phone. I spend my time troubleshooting the unit in front of me instead of searching for what to check next.',
  },
]

export const faqs = [
  {
    q: 'Is this a physical product?',
    a: 'No. HVAC DIAGNOSTIC READY™ is a 100% digital professional resource.',
  },
  {
    q: 'Who is this for?',
    a: 'HVAC technicians and professionals looking for organized, visual and ready-to-use troubleshooting resources.',
  },
  {
    q: 'Is this a certification or training program?',
    a: 'No. It is a professional diagnostic and reference resource, not a certification or substitute for required technical education.',
  },
  {
    q: 'Is this another theory-heavy ebook?',
    a: 'No. The core system focuses on practical visual diagnostic maps, troubleshooting flows, component references and ready-to-use field resources.',
  },
  {
    q: 'Do I need to create the diagnostic maps myself?',
    a: 'No. The maps and resources are already created and organized. You select and use the appropriate resources according to the equipment, symptom and professional judgment.',
  },
  {
    q: 'What areas are included?',
    a: 'Cooling System Diagnostics, Airflow & Indoor Comfort, Electrical & Control, Refrigerant & Pressure, Compressor & Outdoor Unit, and HVAC Field Troubleshooting Maps.',
  },
  {
    q: 'Can I use the resources with different HVAC systems?',
    a: 'The resources provide general troubleshooting frameworks that can be used as references while following the specific manufacturer documentation for the equipment being serviced.',
  },
  {
    q: 'Does it include field-ready resources?',
    a: 'Yes. The system includes visual resources designed for practical digital consultation and reference.',
  },
  {
    q: 'Does it include testing and organization resources?',
    a: 'Yes. The four complementary bonuses include field testing, preventive maintenance, service-call organization and quick-reference tools.',
  },
  {
    q: "Is it tied to one country's HVAC standards?",
    a: 'No. It is an English-language professional resource designed for an international market and is not intended to replace local regulations, certifications, codes or manufacturer requirements.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
]

export interface PurchaseNotification {
  name: string
  location: string
}

export const purchaseNotifications: PurchaseNotification[] = [
  { name: 'James W.', location: 'Manchester, United Kingdom' },
  { name: 'Kyle R.', location: 'Calgary, Canada' },
  { name: 'Miguel S.', location: 'Houston, United States' },
  { name: 'Daniel T.', location: 'Brisbane, Australia' },
  { name: 'Craig M.', location: 'Leeds, United Kingdom' },
  { name: 'Brandon L.', location: 'Toronto, Canada' },
  { name: 'Shawn P.', location: 'Phoenix, United States' },
  { name: 'Nathan K.', location: 'Perth, Australia' },
  { name: 'Aaron D.', location: 'Birmingham, United Kingdom' },
  { name: 'Travis H.', location: 'Edmonton, Canada' },
  { name: 'Eric J.', location: 'Tampa, United States' },
  { name: 'Luke F.', location: 'Melbourne, Australia' },
]
